import React, { useState, useEffect } from "react";
import {
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  TextField,
  Typography,
  Grid,
  Chip,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Alert,
  useTheme,
  Switch,
  FormControlLabel,
} from "@mui/material";
import {
  CloudUpload,
  InsertDriveFile,
  Close,
  Description,
} from "@mui/icons-material";
import axios from "../utils/axiosInstance";
import { useAuth } from "../utils/AuthContext";
import { MANUFACTURER_SOURCES } from "../config/manufacturers";

const generateFileId = async () => {
  try {
    const response = await axios.get(`/file/generate-file-id`);
    return response.data.file_id;
  } catch (error) {
    console.error("Failed to generate file ID:", error);
    return Date.now().toString(36).slice(-5).toUpperCase();
  }
};

const segments = [
  "usa",
  "alabama",
  "alaska",
  "arizona",
  "arkansas",
  "california",
  "colorado",
  "connecticut",
  "delaware",
  "florida",
  "georgia",
  "hawaii",
  "idaho",
  "illinois",
  "indiana",
  "iowa",
  "kansas",
  "kentucky",
  "louisiana",
  "maine",
  "maryland",
  "massachusetts",
  "michigan",
  "minnesota",
  "mississippi",
  "missouri",
  "montana",
  "nebraska",
  "nevada",
  "new hampshire",
  "new jersey",
  "new mexico",
  "new york",
  "north carolina",
  "north dakota",
  "ohio",
  "oklahoma",
  "oregon",
  "pennsylvania",
  "rhode island",
  "south carolina",
  "south dakota",
  "tennessee",
  "texas",
  "utah",
  "vermont",
  "virginia",
  "washington",
  "west virginia",
  "wisconsin",
  "wyoming",
];

const periods = [
  "Q1",
  "Q2",
  "Q3",
  "Q4",
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const currentYear = new Date().getFullYear();
const years = Array.from({ length: 11 }, (_, i) => currentYear - i);

const initialFormData = (manufacturer) => ({
  manufacturer,
  fileId: "",
  source: "",
  segment: segments[0],
  year: "",
  period: "",
  transmissionDate: new Date().toISOString().split("T")[0],
  salesContent: false,
});

const StructuredFileUpload = ({ currentPath, onUpload }) => {
  const theme = useTheme();
  const { user } = useAuth();

  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const manufacturer = user?.manufacturer || "";

  // ✅ Sources driven by manufacturer config — no hardcoded list
  const sources = MANUFACTURER_SOURCES[manufacturer] || [];

  const [formData, setFormData] = useState(initialFormData(manufacturer));

  useEffect(() => {
    setFormData((prev) => ({ ...prev, manufacturer }));
  }, [manufacturer]);

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleFileSelect = (e) => {
    setError(null);
    setFile(e.target.files[0] || null);
  };

  const removeFile = () => setFile(null);

  /* =========================
     Filename generation
  ========================== */

  const generateFileName = (originalFile, fileIdOverride = null) => {
    const ext = originalFile.name.split(".").pop();
    const {
      manufacturer,
      source,
      segment,
      year,
      period,
      transmissionDate,
      salesContent,
    } = formData;
    const fileId = fileIdOverride || formData.fileId || "[FILE-ID]";
    const txDate = transmissionDate.replace(/-/g, "");

    if (salesContent) {
      return `${manufacturer}_${fileId}_844cb_${year}_${period}_${txDate}.${ext}`;
    }
    return `${manufacturer}_${fileId}_claims_${segment}_${source}_${year}_${period}_${txDate}.${ext}`;
  };

  const getPreviewName = () => {
    if (!file) return null;
    return { original: file.name, generated: generateFileName(file) };
  };

  /* =========================
     Validation
  ========================== */

  const validateForm = () => {
    if (!formData.manufacturer) return setError("Manufacturer is required");
    if (!formData.year) return setError("Year is required");
    if (!formData.period) return setError("Period is required");
    if (!formData.transmissionDate)
      return setError("Transmission date required");
    if (!formData.salesContent && !formData.segment)
      return setError("Segment required");
    if (!formData.salesContent && !formData.source)
      return setError("Source required");
    if (!formData.salesContent && sources.length === 0)
      return setError("No sources configured for this manufacturer");
    if (!file) return setError("Please select a file");
    return true;
  };

  const isFormValid = () => {
    const base =
      formData.manufacturer &&
      formData.year &&
      formData.period &&
      formData.transmissionDate &&
      file;

    if (formData.salesContent) return base;
    return base && formData.segment && formData.source && sources.length > 0;
  };

  /* =========================
     Upload handler
  ========================== */

  const handleUpload = async () => {
    if (!validateForm()) return;

    setUploading(true);
    setError(null);

    try {
      let fileId = formData.fileId;
      if (!fileId) {
        fileId = await generateFileId();
        setFormData((prev) => ({ ...prev, fileId }));
      }

      let uploadPath = currentPath;

      const today = new Date();
      const month = String(today.getMonth() + 1).padStart(2, "0");
      const day = String(today.getDate()).padStart(2, "0");
      const year = today.getFullYear();
      const datePath = `uploads/${month}-${day}-${year}`;

      if (Array.isArray(uploadPath)) {
        if (!uploadPath.length || uploadPath.join("/") === "uploads") {
          uploadPath = datePath;
        } else {
          uploadPath = uploadPath.join("/");
        }
      }

      const formDataToSend = new FormData();
      formDataToSend.append("path", uploadPath);

      const newName = generateFileName(file, fileId);
      formDataToSend.append("files", file, newName);
      formDataToSend.append(
        "metadata",
        JSON.stringify({
          ...formData,
          fileId,
          originalFileName: file.name,
          newFileName: newName,
          uploadTimestamp: new Date().toISOString(),
        }),
      );

      await axios.post("/file/upload", formDataToSend, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) =>
          setProgress(Math.round((e.loaded * 100) / e.total)),
      });

      onUpload?.();
      setOpen(false);
      setFile(null);
      setFormData(initialFormData(manufacturer));
    } catch (err) {
      console.error(err);
      setError("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  /* =========================
     Render
  ========================== */

  return (
    <Box>
      <Button
        variant="outlined"
        startIcon={<Description />}
        onClick={() => setOpen(true)}
      >
        Structured Upload
      </Button>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          Structured File Upload
          <Button onClick={() => setOpen(false)} sx={{ ml: "auto" }}>
            <Close />
          </Button>
        </DialogTitle>

        <DialogContent>
          <FormControlLabel
            control={
              <Switch
                checked={formData.salesContent}
                onChange={(e) =>
                  handleFormChange("salesContent", e.target.checked)
                }
              />
            }
            label="844 Chargeback File"
          />

          <Divider sx={{ my: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Manufacturer"
                value={formData.manufacturer}
                disabled
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="File ID"
                value="Generated on upload"
                disabled
              />
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth required>
                <InputLabel>Year</InputLabel>
                <Select
                  value={formData.year}
                  onChange={(e) => handleFormChange("year", e.target.value)}
                >
                  {years.map((y) => (
                    <MenuItem key={y} value={y}>
                      {y}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth required>
                <InputLabel>Period</InputLabel>
                <Select
                  value={formData.period}
                  onChange={(e) => handleFormChange("period", e.target.value)}
                >
                  {periods.map((p) => (
                    <MenuItem key={p} value={p}>
                      {p}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {!formData.salesContent && (
              <>
                <Grid item xs={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Segment</InputLabel>
                    <Select
                      value={formData.segment}
                      onChange={(e) =>
                        handleFormChange("segment", e.target.value)
                      }
                    >
                      {segments.map((s) => (
                        <MenuItem key={s} value={s}>
                          {s}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={6}>
                  <FormControl fullWidth required error={sources.length === 0}>
                    <InputLabel>Source</InputLabel>
                    <Select
                      value={formData.source}
                      onChange={(e) =>
                        handleFormChange("source", e.target.value)
                      }
                      disabled={sources.length === 0}
                    >
                      {sources.map((s) => (
                        <MenuItem key={s} value={s}>
                          {s}
                        </MenuItem>
                      ))}
                    </Select>
                    {sources.length === 0 && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ mt: 0.5 }}
                      >
                        No sources configured for this manufacturer
                      </Typography>
                    )}
                  </FormControl>
                </Grid>
              </>
            )}

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Transmission Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={formData.transmissionDate}
                onChange={(e) =>
                  handleFormChange("transmissionDate", e.target.value)
                }
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <input
            type="file"
            hidden
            id="file-input"
            onChange={handleFileSelect}
          />
          <label htmlFor="file-input">
            <Button
              variant="outlined"
              component="span"
              startIcon={<CloudUpload />}
            >
              Choose File
            </Button>
          </label>

          {file && (
            <Chip
              sx={{ mt: 2 }}
              label={file.name}
              onDelete={removeFile}
              avatar={
                <Avatar>
                  <InsertDriveFile />
                </Avatar>
              }
            />
          )}

          {file && isFormValid() && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2">
                {getPreviewName().generated}
              </Typography>
            </Box>
          )}

          {uploading && <LinearProgress sx={{ mt: 2 }} />}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!isFormValid() || uploading}
            onClick={handleUpload}
          >
            Upload
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StructuredFileUpload;
